/* eslint-disable */
import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, FormGroup, Label, Input } from 'reactstrap';
import { toast } from 'react-toastify';
import { DatePicker } from 'antd';
import moment from 'moment';
import Select from 'react-select';
import { FaEye, FaSpinner, FaTrashAlt, FaPlus, FaChevronDown, FaChevronUp, FaExternalLinkAlt } from 'react-icons/fa';
import {
  fetchVisionRequestDetails,
  uploadVisionDocuments,
  addItemToInvoice,
  removeItemFromInvoice,
  fetchVisionProcedures,
  removeUploadedDocument,
  rejectVisionCard,
  verifyAutoOrder,
  updateVisionRequest,
} from '../../services/api/cashless-vision';
import { fetchUsersRelative } from '../../services/api/offline-consult';
import { getBlobUrl } from '../../utils/getBlobUrlAndContentType';

const STEP_PATIENT = 'patient';
const STEP_VISION_CARD = 'visionCard';
const STEP_ITEMS = 'items';

const rejectionReasons = [
  { value: 'Vision card more than 6 months old', label: 'Vision card more than 6 months old' },
  { value: 'Name mismatch', label: 'Name mismatch' },
  { value: 'Power not matching', label: 'Power not matching' },
  { value: 'Letterhead missing', label: 'Letterhead missing' },
  { value: 'Document uploaded is not a vision card', label: 'Document uploaded is not a vision card' },
  { value: 'Other', label: 'Other' },
];

const sectionHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 20px',
  cursor: 'pointer',
  borderBottom: '1px solid #E0E0E0',
  backgroundColor: '#FAFAFA',
  fontFamily: 'Inter, sans-serif',
  fontSize: '15px',
  fontWeight: '600',
  color: '#0F0B28',
};

const VerifyAutoOrderModal = ({ isOpen, toggle, requestId, onVerified }) => {
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const visionCardInputRef = useRef(null);

  const [expanded, setExpanded] = useState({
    [STEP_PATIENT]: true,
    [STEP_VISION_CARD]: true,
    [STEP_ITEMS]: true,
  });

  // Patient
  const [patientName, setPatientName] = useState('');
  const [userId, setUserId] = useState('');
  const [relativeId, setRelativeId] = useState(null);
  const [relatives, setRelatives] = useState([]);
  const [relativesLoading, setRelativesLoading] = useState(false);
  const [selectedRelative, setSelectedRelative] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [lenskartOrderNo, setLenskartOrderNo] = useState('');

  // Vision card
  const [visionCardFiles, setVisionCardFiles] = useState([]);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState(null);
  const [customReason, setCustomReason] = useState('');

  // Order details
  const [orderDate, setOrderDate] = useState(null);
  const [shippingAddress, setShippingAddress] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [initialOrderDetails, setInitialOrderDetails] = useState({});

  // Items
  const [procedures, setProcedures] = useState([]);
  const [items, setItems] = useState([
    { type: null, qty: '', totalPrice: '', discount: '', price: '', approved: '', isNew: true },
  ]);
  const originalItemsRef = useRef([]);

  const toggleSection = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const fetchDetails = async () => {
    if (!requestId) return;
    try {
      setLoading(true);

      const [response, proceduresResponse] = await Promise.all([
        fetchVisionRequestDetails(requestId),
        fetchVisionProcedures(requestId),
      ]);

      // Parse request details — same structure as orderDetailsModal
      const { requestDetails, preAuthItemDetails, uploadedFiles, walletDetails } = response;
      setWalletBalance(walletDetails?.walletBalance ?? null);

      setLenskartOrderNo(requestDetails?.lenkskartOrderId || '');
      setPatientName(requestDetails?.patientName || requestDetails?.userName || '');
      setUserId(requestDetails?.userId || '');
      setRelativeId(requestDetails?.relativeId || null);

      const parsedOrderDate = requestDetails?.orderDate
        ? moment(requestDetails.orderDate).format('YYYY-MM-DD')
        : null;
      const parsedShipping = requestDetails?.lenskartShippingAddress || '';
      const parsedBilling = requestDetails?.lenskartBillingAddress || '';
      setOrderDate(parsedOrderDate);
      setShippingAddress(parsedShipping);
      setBillingAddress(parsedBilling);
      setInitialOrderDetails({
        orderDate: parsedOrderDate,
        shippingAddress: parsedShipping,
        billingAddress: parsedBilling,
      });

      // Vision card files
      const cardFiles = (uploadedFiles?.visionCard || []).map((file, index) => ({
        url: file.url,
        documentId: file.documentId,
        name: `Vision Card ${index + 1}`,
      }));
      setVisionCardFiles(cardFiles);

      // Pre-auth items
      const mappedItems = (preAuthItemDetails || []).map((item) => ({
        type: { value: item.procedureId, label: item.itemName },
        qty: item.quantity?.toString() || '',
        totalPrice: item.totalPrice?.toString() || '',
        discount: item.discount?.toString() || '',
        price: item.cost?.toString() || '',
        approved: item.approvedAmount?.toString() || '',
        procedureId: item.procedureId,
        visionProcedureId: item.visionProcedureId,
        isNew: false,
      }));
      const finalItems = mappedItems.length > 0
        ? mappedItems
        : [{ type: null, qty: '', totalPrice: '', discount: '', price: '', approved: '', isNew: true }];
      setItems(finalItems);
      originalItemsRef.current = mappedItems;

      // Procedures
      if (proceduresResponse.message === 'success') {
        setProcedures(
          (proceduresResponse.data || []).map((proc) => ({
            value: proc.procedureId,
            label: proc.displayName,
            isCovered: proc.isCovered,
          }))
        );
      }

      // Fetch relatives after we have userId
      if (requestDetails?.userId) {
        fetchRelatives(requestDetails.userId, requestDetails.relativeId);
      }
    } catch (err) {
      toast.error('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatives = async (uid, currentRelativeId) => {
    try {
      setRelativesLoading(true);
      const relativesResponse = await fetchUsersRelative(uid);
      const relativeOptions = (relativesResponse?.data || []).map((r) => ({
        value: r.relativeId,
        label: r.relativeName,
      }));
      setRelatives(relativeOptions);
      if (currentRelativeId) {
        const match = relativeOptions.find((o) => o.value === currentRelativeId);
        setSelectedRelative(match || null);
      } else {
        setSelectedRelative({ value: 'self', label: 'Self (No Dependent)' });
      }
    } catch {
      toast.error('Error fetching relatives');
    } finally {
      setRelativesLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchDetails();
  }, [isOpen, requestId]);

  // Vision card handlers
  const handleFileUpload = () => visionCardInputRef.current?.click();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('visionCard', file);
    try {
      setLoading(true);
      const res = await uploadVisionDocuments(requestId, fd);
      if (res.message === 'success') {
        toast.success('Vision card uploaded');
        await fetchDetails();
      } else {
        toast.error('Failed to upload vision card');
      }
    } catch {
      toast.error('Failed to upload vision card');
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (documentId) => {
    try {
      setLoading(true);
      const res = await removeUploadedDocument(requestId, documentId);
      if (res.message === 'success') {
        toast.success('File removed');
        await fetchDetails();
      } else {
        toast.error('Failed to remove file');
      }
    } catch {
      toast.error('Failed to remove file');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectVisionCard = async () => {
    if (!rejectReason) {
      toast.error('Please select a rejection reason');
      return;
    }
    const finalReason = rejectReason.value === 'Other' ? customReason : rejectReason.value;
    if (!finalReason) {
      toast.error('Please enter a rejection reason');
      return;
    }
    try {
      setLoading(true);
      await rejectVisionCard(requestId, finalReason);
      toast.success('Vision card rejected');
      setShowRejectForm(false);
      setRejectReason(null);
      setCustomReason('');
      await fetchDetails();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject vision card');
    } finally {
      setLoading(false);
    }
  };

  // Items handlers
  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddItem = async (index) => {
    const item = items[index];
    if (!item.type || !item.qty || !item.totalPrice || !item.price || !item.approved) {
      toast.error('Please fill all item details before adding');
      return;
    }
    try {
      setLoading(true);
      const res = await addItemToInvoice(requestId, {
        visionProcedureId: item.type.value,
        itemName: item.type.label,
        cost: item.price,
        totalPrice: item.totalPrice,
        discount: item.discount,
        approvedAmount: item.approved,
        quantity: item.qty,
        remark: null,
      });
      if (res.message === 'success') {
        toast.success('Item added');
      } else {
        toast.error(res.errorMessage || 'Failed to add item');
      }
    } catch {
      toast.error('Failed to add item');
    } finally {
      await fetchDetails();
      setLoading(false);
    }
  };

  const handleRemoveItem = async (index) => {
    const item = items[index];
    if (item.isNew) {
      setItems((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    try {
      setLoading(true);
      const res = await removeItemFromInvoice(requestId, item.procedureId);
      if (res.message === 'success') {
        toast.success('Item removed');
      } else {
        toast.error('Failed to remove item');
      }
    } catch {
      toast.error('Failed to remove item');
    } finally {
      await fetchDetails();
      setLoading(false);
    }
  };

  const handleAddNewRow = () => {
    if (items.some((i) => i.isNew)) return;
    setItems((prev) => [
      ...prev,
      { type: null, qty: '', totalPrice: '', discount: '', price: '', approved: '', isNew: true },
    ]);
  };

  // Submit
  const savedItems = items.filter((i) => !i.isNew);
  const orderDetailsComplete = !!orderDate && !!shippingAddress && !!billingAddress;

  const handleSubmit = async () => {
    if (!orderDate) {
      toast.error('Please enter the order date');
      setExpanded((prev) => ({ ...prev, [STEP_ITEMS]: true }));
      return;
    }
    if (!shippingAddress) {
      toast.error('Please enter the shipping address');
      setExpanded((prev) => ({ ...prev, [STEP_ITEMS]: true }));
      return;
    }
    if (!billingAddress) {
      toast.error('Please enter the billing address');
      setExpanded((prev) => ({ ...prev, [STEP_ITEMS]: true }));
      return;
    }
    if (savedItems.length === 0) {
      toast.error('At least one item must be added before verifying');
      setExpanded((prev) => ({ ...prev, [STEP_ITEMS]: true }));
      return;
    }
    const missingApproved = savedItems.find((i) => !i.approved);
    if (missingApproved) {
      toast.error(`Approved amount is required for all items`);
      setExpanded((prev) => ({ ...prev, [STEP_ITEMS]: true }));
      return;
    }
    try {
      setVerifyLoading(true);

      // Save order details if any changed
      const lenskartOrderDetails = {};
      if (orderDate !== initialOrderDetails.orderDate) lenskartOrderDetails.orderDate = orderDate;
      if (shippingAddress !== initialOrderDetails.shippingAddress) lenskartOrderDetails.shippingAddress = shippingAddress;
      if (billingAddress !== initialOrderDetails.billingAddress) lenskartOrderDetails.billingAddress = billingAddress;
      if (Object.keys(lenskartOrderDetails).length > 0) {
        await updateVisionRequest(requestId, { lenskartOrderDetails });
      }

      // Save dirty existing items (remove + re-add with correct visionProcedureId)
      const dirtyItems = savedItems.filter((item) => {
        const orig = originalItemsRef.current.find((o) => o.procedureId === item.procedureId);
        if (!orig) return false;
        return (
          item.type?.value !== orig.type?.value ||
          item.qty !== orig.qty ||
          item.totalPrice !== orig.totalPrice ||
          item.discount !== orig.discount ||
          item.price !== orig.price ||
          item.approved !== orig.approved
        );
      });
      for (const item of dirtyItems) {
        const orig = originalItemsRef.current.find((o) => o.procedureId === item.procedureId);
        // If ops changed the procedure type, item.type.value is the new cvp.id from the dropdown.
        // Otherwise use the stored visionProcedureId (vpar.vision_procedure_id) for the same procedure.
        const effectiveVisionProcedureId = item.type?.value !== orig.type?.value
          ? item.type.value
          : item.visionProcedureId;
        await removeItemFromInvoice(requestId, item.procedureId);
        await addItemToInvoice(requestId, {
          visionProcedureId: effectiveVisionProcedureId,
          itemName: item.type.label,
          cost: item.price,
          totalPrice: item.totalPrice,
          discount: item.discount,
          approvedAmount: item.approved,
          quantity: item.qty,
          remark: null,
        });
      }

      const payload = {
        relativeId: selectedRelative?.value === 'self' ? null : (selectedRelative?.value ?? null),
      };
      await verifyAutoOrder(requestId, payload);
      toast.success('Order verified. User has been notified.');
      toggle();
      if (onVerified) onVerified();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to verify order');
    } finally {
      setVerifyLoading(false);
    }
  };

  // Render helpers
  const renderStep = (key, label, stepNumber, content, isComplete, isMandatory) => (
    <div style={{ borderBottom: '1px solid #E0E0E0' }}>
      <div style={sectionHeaderStyle} onClick={() => toggleSection(key)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{
            backgroundColor: isComplete ? '#2E7D32' : '#6C5DD3',
            color: '#fff',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: '700',
            flexShrink: 0,
          }}>
            {isComplete ? '✓' : stepNumber}
          </span>
          <span>{label}</span>
          {isMandatory && !isComplete && (
            <span style={{ color: '#FF4D4D', fontSize: '11px', fontWeight: '400' }}>required</span>
          )}
        </div>
        {expanded[key]
          ? <FaChevronUp size={14} color="#6C5DD3" />
          : <FaChevronDown size={14} color="#6C5DD3" />}
      </div>
      {expanded[key] && <div style={{ padding: '20px' }}>{content}</div>}
    </div>
  );

  // Step 1 — Patient
  const patientStep = (
    <div>
      <FormGroup>
        <Label style={{ fontSize: '13px', fontWeight: '500', fontFamily: 'Inter, sans-serif', color: '#0F0B28' }}>
          Patient Name
        </Label>
        <Input
          value={patientName}
          readOnly
          style={{ backgroundColor: '#F5F5F5', cursor: 'not-allowed', fontFamily: 'Inter, sans-serif' }}
        />
      </FormGroup>
      <FormGroup>
        <Label style={{ fontSize: '13px', fontWeight: '500', fontFamily: 'Inter, sans-serif', color: '#0F0B28' }}>
          Select Correct Patient / Relative <span style={{ color: 'red' }}>*</span>
        </Label>
        <Select
          options={[{ value: 'self', label: 'Self (No Dependent)' }, ...relatives]}
          value={selectedRelative}
          onChange={(opt) => setSelectedRelative(opt)}
          isLoading={relativesLoading}
          placeholder="Select patient or relative..."
          isClearable
        />
        <small style={{ color: '#888', fontFamily: 'Inter, sans-serif', fontSize: '12px' }}>
          Change only if the prefilled patient is incorrect.
        </small>
      </FormGroup>
      {walletBalance !== null && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#F2F0FF',
          borderRadius: '8px',
          padding: '10px 16px',
          marginBottom: '14px',
        }}>
          <span style={{ fontSize: '13px', fontFamily: 'Inter, sans-serif', color: '#0F0B28', fontWeight: '500' }}>
            Wallet Balance
          </span>
          <span style={{ fontSize: '15px', fontFamily: 'Inter, sans-serif', color: '#6C5DD3', fontWeight: '700' }}>
            ₹ {walletBalance}
          </span>
        </div>
      )}
    </div>
  );

  // Step 2 — Vision Card
  const visionCardStep = (
    <div>
      {visionCardFiles.length > 0 ? (
        <div>
          <Label style={{ fontSize: '13px', fontWeight: '500', fontFamily: 'Inter, sans-serif', color: '#0F0B28', marginBottom: '10px', display: 'block' }}>
            Uploaded Vision Card(s)
          </Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
            {visionCardFiles.map((file, i) => (
              <div key={i} style={{
                backgroundColor: '#714FFF19',
                padding: '8px 12px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a
                  onClick={async () => {
                    const url = await getBlobUrl(file.url);
                    window.open(url, '_blank');
                  }}
                  style={{ color: 'blue', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                >
                  <FaEye size={16} />
                </a>
                <span style={{ fontSize: '13px', color: '#0F0B28', fontFamily: 'Inter, sans-serif' }}>
                  {file.name}
                </span>
                <span
                  onClick={() => handleDeleteFile(file.documentId)}
                  style={{ cursor: 'pointer', color: '#999', fontSize: '18px', lineHeight: 1 }}
                >×</span>
              </div>
            ))}
          </div>

          {!showRejectForm ? (
            <Button
              color="danger"
              outline
              onClick={() => setShowRejectForm(true)}
              style={{
                borderRadius: '8px',
                borderColor: '#FF4D4D',
                color: '#FF4D4D',
                fontSize: '13px',
                fontWeight: '500',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Reject Vision Card
            </Button>
          ) : (
            <div style={{
              backgroundColor: '#FFF5F5',
              border: '1px solid #FFD0D0',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '8px',
            }}>
              <FormGroup>
                <Label style={{ fontSize: '13px', fontWeight: '500', fontFamily: 'Inter, sans-serif' }}>
                  Rejection Reason
                </Label>
                <Select
                  options={rejectionReasons}
                  value={rejectReason}
                  onChange={setRejectReason}
                  placeholder="Select reason..."
                />
              </FormGroup>
              {rejectReason?.value === 'Other' && (
                <FormGroup>
                  <Input
                    type="textarea"
                    placeholder="Enter custom reason..."
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    style={{ fontSize: '13px', fontFamily: 'Inter, sans-serif' }}
                  />
                </FormGroup>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  disabled={loading}
                  onClick={handleRejectVisionCard}
                  style={{
                    backgroundColor: '#FF4D4D',
                    border: 'none',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: '600',
                    fontFamily: 'Inter, sans-serif',
                    borderRadius: '6px',
                  }}
                >
                  {loading ? <FaSpinner className="icon-spin" /> : 'Confirm Rejection'}
                </Button>
                <Button
                  onClick={() => { setShowRejectForm(false); setRejectReason(null); setCustomReason(''); }}
                  style={{
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    color: '#666',
                    fontSize: '13px',
                    fontFamily: 'Inter, sans-serif',
                    borderRadius: '6px',
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div>
          <p style={{ fontSize: '13px', color: '#888', fontFamily: 'Inter, sans-serif', marginBottom: '12px' }}>
            No vision card uploaded yet. You can upload one now, or proceed — the user will be prompted to upload after verification.
          </p>
          <Button
            outline
            onClick={handleFileUpload}
            disabled={loading}
            style={{
              color: '#714FFF',
              borderColor: '#714FFF',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '500',
              fontFamily: 'Inter, sans-serif',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <FaPlus size={12} /> Upload Vision Card
          </Button>
        </div>
      )}
      <input
        type="file"
        ref={visionCardInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        accept="image/*,application/pdf"
      />
    </div>
  );

  // Step 3 — Order Details + Items
  const itemsStep = (
    <div>
      {/* Order Details */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', fontWeight: '600', fontFamily: 'Inter, sans-serif', color: '#0F0B28', marginBottom: '12px' }}>
          Order Details
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
          <FormGroup style={{ margin: 0 }}>
            <Label style={{ fontSize: '13px', fontWeight: '500', fontFamily: 'Inter, sans-serif', color: '#0F0B28' }}>
              Order Date <span style={{ color: 'red' }}>*</span>
            </Label>
            <DatePicker
              value={orderDate ? moment(orderDate) : null}
              onChange={(date) => setOrderDate(date ? date.format('YYYY-MM-DD') : null)}
              style={{ width: '100%', height: '38px', borderRadius: '8px', fontSize: '13px' }}
              placeholder="Select order date"
            />
          </FormGroup>
          <FormGroup style={{ margin: 0 }}>
            <Label style={{ fontSize: '13px', fontWeight: '500', fontFamily: 'Inter, sans-serif', color: '#0F0B28' }}>
              Shipping Address <span style={{ color: 'red' }}>*</span>
            </Label>
            <Input
              value={shippingAddress}
              onChange={(e) => setShippingAddress(e.target.value)}
              placeholder="Enter shipping address"
              style={{ height: '38px', borderRadius: '8px', fontSize: '13px' }}
            />
          </FormGroup>
        </div>
        <FormGroup style={{ margin: 0 }}>
          <Label style={{ fontSize: '13px', fontWeight: '500', fontFamily: 'Inter, sans-serif', color: '#0F0B28' }}>
            Billing Address <span style={{ color: 'red' }}>*</span>
          </Label>
          <Input
            value={billingAddress}
            onChange={(e) => setBillingAddress(e.target.value)}
            placeholder="Enter billing address"
            style={{ height: '38px', borderRadius: '8px', fontSize: '13px' }}
          />
        </FormGroup>
      </div>

      {/* Items */}
    <div style={{ backgroundColor: '#F0F3F6', borderRadius: '8px', padding: '16px' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 0.5fr 1.5fr 1.5fr 1.5fr 1.5fr 0.5fr',
        gap: '12px',
        marginBottom: '12px',
        fontSize: '13px',
        fontWeight: '600',
        fontFamily: 'Inter, sans-serif',
        color: '#0F0B28',
      }}>
        <div>Item</div>
        <div>Qty</div>
        <div>Total Price</div>
        <div>Discount</div>
        <div>Price</div>
        <div>Approved</div>
        <div>Action</div>
      </div>

      {items.map((item, index) => (
        <div key={index} style={{
          display: 'grid',
          gridTemplateColumns: '2fr 0.5fr 1.5fr 1.5fr 1.5fr 1.5fr 0.5fr',
          gap: '12px',
          marginBottom: '10px',
          alignItems: 'center',
        }}>
          <Select
            value={item.type}
            onChange={(sel) => handleItemChange(index, 'type', sel)}
            options={procedures}
            styles={{
              control: (base) => ({
                ...base,
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                minHeight: '38px',
                border: '1px solid #E0E0E0',
                fontSize: '13px',
              }),
              singleValue: (base) => ({
                ...base,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
              option: (provided, state) => ({
                ...provided,
                fontSize: '13px',
                backgroundColor: state.data.isCovered ? '#B3FFB3' : '#FFB3B3',
                color: '#0F0B28',
                '&:hover': {
                  backgroundColor: state.data.isCovered ? '#A0DFA0' : '#FF9999',
                },
              }),
            }}
          />
          {['qty', 'totalPrice', 'discount', 'price', 'approved'].map((field) => (
            <Input
              key={field}
              value={item[field]}
              onChange={(e) => handleItemChange(index, field, e.target.value)}
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '8px',
                height: '38px',
                fontSize: '13px',
                border: '1px solid #E0E0E0',
              }}
            />
          ))}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {item.isNew ? (
              <FaPlus
                size={16}
                style={{ cursor: 'pointer', color: '#4CAF50' }}
                onClick={() => handleAddItem(index)}
              />
            ) : (
              <FaTrashAlt
                size={14}
                style={{ cursor: 'pointer', color: '#FF4D4D' }}
                onClick={() => handleRemoveItem(index)}
              />
            )}
          </div>
        </div>
      ))}

      {savedItems.length > 0 && (() => {
        const sum = (field) =>
          savedItems.reduce((acc, i) => acc + (parseFloat(i[field]) || 0), 0);
        const fmt = (val) => val % 1 === 0 ? val.toFixed(0) : val.toFixed(2);
        return (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 0.5fr 1.5fr 1.5fr 1.5fr 1.5fr 0.5fr',
            gap: '12px',
            marginTop: '8px',
            borderTop: '1px solid #D0D0D0',
            paddingTop: '10px',
          }}>
            <div style={{ fontSize: '12px', fontWeight: '700', fontFamily: 'Inter, sans-serif', color: '#0F0B28' }}>
              Total
            </div>
            <div />
            {['totalPrice', 'discount', 'price', 'approved'].map((field) => (
              <div key={field} style={{ fontSize: '12px', fontWeight: '700', fontFamily: 'Inter, sans-serif', color: '#6C5DD3' }}>
                ₹ {fmt(sum(field))}
              </div>
            ))}
            <div />
          </div>
        );
      })()}

      <Button
        outline
        onClick={handleAddNewRow}
        style={{
          marginTop: '12px',
          color: '#714FFF',
          borderColor: '#714FFF',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: '500',
          fontFamily: 'Inter, sans-serif',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <FaPlus size={12} /> Add Item
      </Button>
    </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px 20px',
        borderBottom: '1px solid #E0E0E0',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{
            color: '#714FFF',
            fontSize: '18px',
            fontWeight: '600',
            fontFamily: 'Inter, sans-serif',
          }}>
            Verify Auto-Created Order
          </span>
          <span style={{
            backgroundColor: '#6C5DD3',
            color: '#fff',
            padding: '3px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            fontFamily: 'Inter, sans-serif',
          }}>
            Auto Created
          </span>
          {lenskartOrderNo && (
            <a
              href={`https://www.lenskart.com/insurance-dashboard/detail/?orderId=${lenskartOrderNo}&type=ORDER`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                backgroundColor: '#714FFF',
                color: '#fff',
                fontSize: '12px',
                fontWeight: '500',
                fontFamily: 'Inter, sans-serif',
                padding: '4px 10px',
                borderRadius: '6px',
                textDecoration: 'none',
              }}
            >
              Go to Lenskart <FaExternalLinkAlt size={10} />
            </a>
          )}
        </div>
        <span
          onClick={toggle}
          style={{ cursor: 'pointer', fontSize: '24px', color: '#666', fontWeight: '300' }}
        >×</span>
      </div>

      {/* Info banner */}
      <div style={{
        backgroundColor: '#FFF8E1',
        borderLeft: '4px solid #F59E0B',
        padding: '10px 20px',
        fontSize: '13px',
        color: '#666',
        fontFamily: 'Inter, sans-serif',
      }}>
        Review each section below. After verification, the user will be notified based on vision card status.
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <FaSpinner className="icon-spin" size={24} color="#714FFF" />
        </div>
      )}

      {!loading && (
        <>
          {renderStep(STEP_PATIENT, 'Step 1 — Verify Patient', 1, patientStep, !!selectedRelative, false)}
          {renderStep(STEP_VISION_CARD, 'Step 2 — Vision Card', 2, visionCardStep, false, false)}
          {renderStep(STEP_ITEMS, 'Step 3 — Order Details & Items', 3, itemsStep, orderDetailsComplete && savedItems.length > 0, true)}

          <div style={{ padding: '20px' }}>
            <Button
              onClick={handleSubmit}
              disabled={verifyLoading || !orderDetailsComplete || savedItems.length === 0}
              style={{
                backgroundColor: '#6C5DD3',
                border: 'none',
                color: '#FFFFFF',
                width: '100%',
                height: '44px',
                fontSize: '15px',
                fontWeight: '600',
                fontFamily: 'Inter, sans-serif',
                borderRadius: '8px',
              }}
            >
              {verifyLoading ? <FaSpinner className="icon-spin" /> : 'Verify & Submit'}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default VerifyAutoOrderModal;
